/**
 * @fileoverview Shared callable API contract for
 * `recordExternalSectionAttendance`, which records a section join for a course
 * that hosts its own Zoom (`usesExternalZoom`). Imported by both the functions
 * handler and the webapp client so the two sides cannot drift in name, request
 * shape, or response shape.
 *
 * Courses on CIP-managed Zoom record their attendance inside
 * `createZoomMeeting` instead; this callable exists because an external-Zoom
 * join never reaches that function. See
 * [#2699](https://github.com/CodeinPlace/cip/issues/2699).
 */
import {z} from 'zod';

import {FirestoreDocumentIdSchema} from '../firebase/types';

/**
 * Deployed Cloud Function name. Must match the exported function name in the
 * functions codebase so deployment and invocation resolve to the same
 * function.
 */
export const RECORD_EXTERNAL_SECTION_ATTENDANCE_FUNCTION_NAME =
  'recordExternalSectionAttendance';

/**
 * Server-side function timeout (`timeoutSeconds`). The handler reads four
 * documents and writes a small batch.
 */
export const RECORD_EXTERNAL_SECTION_ATTENDANCE_SERVER_TIMEOUT_SECONDS = 20;

/**
 * How much longer the client waits than the server may run, so a call that
 * commits near the server deadline still returns its result rather than
 * timing out on the client. Covers the round trip and a cold start.
 */
const RECORD_EXTERNAL_SECTION_ATTENDANCE_CLIENT_TIMEOUT_MARGIN_MS = 5_000;

/** Client-side callable timeout. */
export const RECORD_EXTERNAL_SECTION_ATTENDANCE_TIMEOUT_MS =
  RECORD_EXTERNAL_SECTION_ATTENDANCE_SERVER_TIMEOUT_SECONDS * 1000 +
  RECORD_EXTERNAL_SECTION_ATTENDANCE_CLIENT_TIMEOUT_MARGIN_MS;

/** What the client sends when a section leader or student joins. */
export const RecordExternalSectionAttendanceRequestSchema = z.object({
  /** The section being joined. */
  sectionId: FirestoreDocumentIdSchema,
  /** The course the section belongs to. */
  courseId: FirestoreDocumentIdSchema,
});

/** Parsed request. */
export type RecordExternalSectionAttendanceRequest = z.infer<
  typeof RecordExternalSectionAttendanceRequestSchema
>;

/**
 * Why a call recorded nothing. The client ignores these — joining the Zoom
 * must never depend on attendance succeeding — so they exist for logs and
 * tests.
 */
export type RecordExternalSectionAttendanceRefusal =
  /** The caller holds no role in the course, so has nothing to record. */
  | 'not-in-course'
  /** The course does not host its own Zoom, so `createZoomMeeting` owns this. */
  | 'not-external-zoom'
  /** Now is outside the join window for the section week being recorded. */
  | 'outside-window'
  /** The section or course document is missing, or they disagree. */
  | 'unknown-section'
  /** The caller's account is being deleted. */
  | 'account-deleting';

/** What the callable returns. */
export type RecordExternalSectionAttendanceResponse =
  | {
      /** Attendance was recorded. */
      status: 'recorded';
      /** The 0-based section week it was recorded against. */
      sectionWeekIdx: number;
      /** Whether the joiner was marked a guest of the section. */
      markedGuest: boolean;
      /** Whether the week's `teacherAttendance` bit was flipped. */
      markedTeacher: boolean;
      /** Whether a Canvas attendance grade was written alongside. */
      wroteCanvasGrade: boolean;
    }
  | {
      /** Nothing was recorded. */
      status: 'refused';
      /** Why. */
      reason: RecordExternalSectionAttendanceRefusal;
    };
